package com.rainbowclubs.rainbow.presentation.membership

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.Checkbox
import androidx.compose.material3.DatePicker
import androidx.compose.material3.DatePickerDialog
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.rememberDatePickerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import com.rainbowclubs.rainbow.data.models.Club
import com.rainbowclubs.rainbow.data.models.MembershipDataItem
import com.rainbowclubs.rainbow.data.models.MembershipDataTypes
import com.rainbowclubs.rainbow.presentation.ScreenState
import com.rainbowclubs.rainbow.presentation.common.ErrorScreen
import com.rainbowclubs.rainbow.presentation.common.LoadingScreen
import kotlinx.datetime.TimeZone
import kotlinx.datetime.number
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.stringResource
import org.jetbrains.compose.resources.vectorResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import rainbowclubs.composeapp.generated.resources.Res
import rainbowclubs.composeapp.generated.resources.cancel
import rainbowclubs.composeapp.generated.resources.field_required
import rainbowclubs.composeapp.generated.resources.general_error
import rainbowclubs.composeapp.generated.resources.ic_warning
import rainbowclubs.composeapp.generated.resources.no_membership_data_required
import rainbowclubs.composeapp.generated.resources.ok
import rainbowclubs.composeapp.generated.resources.select_date
import rainbowclubs.composeapp.generated.resources.submit_my_data
import rainbowclubs.composeapp.generated.resources.try_again_button
import rainbowclubs.composeapp.generated.resources.warning_icon_cd
import kotlin.time.Instant

@Composable
fun MembershipDataScreen(
    clubId: String,
    navigateTo: (MembershipDataViewModel.NavigationEvent) -> Unit,
    viewModel: MembershipDataViewModel = koinViewModel { parametersOf(clubId) },
) {
    val screenState by viewModel.screenState.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.navigationIntent.collect { intent ->
            navigateTo(intent)
        }
    }

    LaunchedEffect(clubId) {
        viewModel.fetchData()
    }

    when (screenState) {
        is ScreenState.Error -> ErrorScreen(
            error = stringResource(Res.string.general_error),
            onButtonClick = { viewModel.fetchData() },
            buttonText = stringResource(Res.string.try_again_button),
        )

        is ScreenState.Loading -> LoadingScreen()
        is ScreenState.Content -> {
            val club = (screenState as ScreenState.Content<Club>).data
            MembershipDataContent(
                club = club,
                onSubmit = { viewModel.submitMembershipData(it) },
            )
        }
    }
}

@Composable
private fun MembershipDataContent(
    club: Club,
    onSubmit: (Map<String, String>) -> Unit,
    modifier: Modifier = Modifier,
) {
    val membershipData = club.membershipData.orEmpty()
    val aggregatedData = remember { mutableStateMapOf<String, String>() }
    val errors = remember { mutableStateMapOf<String, Boolean>() }

    Column(
        modifier = modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .padding(horizontal = 20.dp, vertical = 16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp),
    ) {
        Text(
            text = club.name,
            style = MaterialTheme.typography.headlineSmall,
        )

        if (membershipData.isEmpty()) {
            Text(
                text = stringResource(Res.string.no_membership_data_required),
                style = MaterialTheme.typography.bodyMedium,
            )
        } else {
            membershipData.forEachIndexed { index, item ->
                key("membership-data-$index-${item.label}") {
                    MembershipField(
                        item = item,
                        index = index,
                        isError = errors[item.label] ?: false,
                        onValueChange = { value ->
                            aggregatedData[item.label] = value
                            errors[item.label] = false
                        },
                    )
                }
            }
        }

        Spacer(modifier = Modifier.height(8.dp))

        Button(
            onClick = {
                var hasError = false
                membershipData.forEach { item ->
                    if (item.required && aggregatedData[item.label].isNullOrBlank()) {
                        errors[item.label] = true
                        hasError = true
                    } else {
                        errors[item.label] = false
                    }
                }

                if (!hasError) {
                    onSubmit(aggregatedData.toMap())
                }
            },
            modifier = Modifier.fillMaxWidth(),
        ) {
            Text(stringResource(Res.string.submit_my_data))
        }
    }
}

private fun formatDate(millis: Long): String {
    // 1. Convert millis to an Instant
    val instant = Instant.fromEpochMilliseconds(millis)

    // 2. Convert to LocalDateTime using the system's timezone
    val dateTime = instant.toLocalDateTime(TimeZone.currentSystemDefault())

    // 3. Manually format the string with padding for leading zeros
    val day = dateTime.day.toString().padStart(2, '0')
    val month = dateTime.month.number.toString().padStart(2, '0')
    val year = dateTime.year

    return "$day-$month-$year"
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun MembershipField(
    item: MembershipDataItem,
    index: Int,
    isError: Boolean,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    val label = if (item.required) "${item.label} *" else item.label

    when (item.type) {
        MembershipDataTypes.SHORT_TEXT -> {
            var value by remember(index) { mutableStateOf("") }
            OutlinedTextField(
                value = value,
                onValueChange = {
                    value = it
                    onValueChange(it)
                },
                label = { Text(label) },
                isError = isError,
                supportingText = if (isError) {
                    { Text(stringResource(Res.string.field_required)) }
                } else null,
                trailingIcon = if (isError) {
                    {
                        Icon(
                            imageVector = vectorResource(Res.drawable.ic_warning),
                            contentDescription = stringResource(Res.string.warning_icon_cd)
                        )
                    }
                } else null,
                modifier = modifier.fillMaxWidth(),
            )
        }

        MembershipDataTypes.UNKNOWN -> {
            Text("unknown")
        }

        MembershipDataTypes.LONG_TEXT -> {
            var value by remember(index) { mutableStateOf("") }
            OutlinedTextField(
                value = value,
                onValueChange = {
                    value = it
                    onValueChange(it)
                },
                label = { Text(label) },
                minLines = 3,
                isError = isError,
                supportingText = if (isError) {
                    { Text(stringResource(Res.string.field_required)) }
                } else null,
                trailingIcon = if (isError) {
                    {
                        Icon(
                            imageVector = vectorResource(Res.drawable.ic_warning),
                            contentDescription = stringResource(Res.string.warning_icon_cd)
                        )
                    }
                } else null,
                modifier = modifier.fillMaxWidth(),
            )
        }

        MembershipDataTypes.NUMBER -> {
            var value by remember(index) { mutableStateOf("") }
            OutlinedTextField(
                value = value,
                onValueChange = {
                    value = it
                    onValueChange(it)
                },
                label = { Text(label) },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                isError = isError,
                supportingText = if (isError) {
                    { Text(stringResource(Res.string.field_required)) }
                } else null,
                trailingIcon = if (isError) {
                    {
                        Icon(
                            imageVector = vectorResource(Res.drawable.ic_warning),
                            contentDescription = stringResource(Res.string.warning_icon_cd)
                        )
                    }
                } else null,
                modifier = modifier.fillMaxWidth(),
            )
        }

        MembershipDataTypes.DATE -> {
            var isDialogOpen by remember(index) { mutableStateOf(false) }
            val datePickerState = rememberDatePickerState()
            val selectedDateText = datePickerState.selectedDateMillis?.let(::formatDate).orEmpty()

            LaunchedEffect(datePickerState.selectedDateMillis) {
                datePickerState.selectedDateMillis?.let {
                    onValueChange(formatDate(it))
                }
            }

            Box(modifier = modifier.fillMaxWidth()) {
                OutlinedTextField(
                    value = selectedDateText,
                    onValueChange = { },
                    readOnly = true,
                    label = { Text(label) },
                    placeholder = { Text(stringResource(Res.string.select_date)) },
                    isError = isError,
                    supportingText = if (isError) {
                        { Text(stringResource(Res.string.field_required)) }
                    } else null,
                    trailingIcon = if (isError) {
                        {
                            Icon(
                                imageVector = vectorResource(Res.drawable.ic_warning),
                                contentDescription = stringResource(Res.string.warning_icon_cd),
                            )
                        }
                    } else null,
                    modifier = Modifier.fillMaxWidth(),
                )
                Box(
                    modifier = Modifier
                        .matchParentSize()
                        .clickable { isDialogOpen = true }
                )
            }
            if (isDialogOpen) {
                DatePickerDialog(
                    onDismissRequest = { isDialogOpen = false },
                    confirmButton = {
                        TextButton(onClick = { isDialogOpen = false }) {
                            Text(stringResource(Res.string.ok))
                        }
                    },
                    dismissButton = {
                        TextButton(onClick = { isDialogOpen = false }) {
                            Text(stringResource(Res.string.cancel))
                        }
                    },
                ) {
                    DatePicker(state = datePickerState)
                }
            }
        }

        MembershipDataTypes.DROP_DOWN -> {
            val options = item.options.orEmpty()
            if (options.isEmpty()) {
                var value by remember(index) { mutableStateOf("") }
                OutlinedTextField(
                    value = value,
                    onValueChange = {
                        value = it
                        onValueChange(it)
                    },
                    label = { Text(label) },
                    isError = isError,
                    supportingText = if (isError) {
                        { Text(stringResource(Res.string.field_required)) }
                    } else null,
                    trailingIcon = if (isError) {
                        {
                            Icon(
                                imageVector = vectorResource(Res.drawable.ic_warning),
                                contentDescription = stringResource(Res.string.warning_icon_cd),
                            )
                        }
                    } else null,
                    modifier = modifier.fillMaxWidth(),
                )
            } else {
                var expanded by remember(index) { mutableStateOf(false) }
                var selected by remember(index) { mutableStateOf("") }
                Box(modifier = modifier.fillMaxWidth()) {
                    OutlinedTextField(
                        value = selected,
                        onValueChange = { },
                        readOnly = true,
                        label = { Text(label) },
                        isError = isError,
                        supportingText = if (isError) {
                            { Text(stringResource(Res.string.field_required)) }
                        } else null,
                        trailingIcon = if (isError) {
                            {
                                Icon(
                                    imageVector = vectorResource(Res.drawable.ic_warning),
                                    contentDescription = stringResource(Res.string.warning_icon_cd),
                                )
                            }
                        } else {
                            null
                        },
                        modifier = Modifier.fillMaxWidth(),
                    )
                    Box(
                        modifier = Modifier
                            .matchParentSize()
                            .clickable { expanded = true }
                    )
                    DropdownMenu(
                        expanded = expanded,
                        onDismissRequest = { expanded = false },
                        modifier = Modifier.fillMaxWidth(),
                    ) {
                        options.forEach { option ->
                            DropdownMenuItem(
                                text = { Text(option) },
                                onClick = {
                                    selected = option
                                    onValueChange(option)
                                    expanded = false
                                },
                            )
                        }
                    }
                }
            }
        }

        MembershipDataTypes.MULTI_SELECT -> {
            val options = item.options.orEmpty()
            if (options.isEmpty()) {
                var value by remember(index) { mutableStateOf("") }
                OutlinedTextField(
                    value = value,
                    onValueChange = {
                        value = it
                        onValueChange(it)
                    },
                    label = { Text(label) },
                    isError = isError,
                    supportingText = if (isError) {
                        { Text(stringResource(Res.string.field_required)) }
                    } else null,
                    trailingIcon = if (isError) {
                        {
                            Icon(
                                imageVector = vectorResource(Res.drawable.ic_warning),
                                contentDescription = stringResource(Res.string.warning_icon_cd),
                            )
                        }
                    } else null,
                    modifier = modifier.fillMaxWidth(),
                )
            } else {
                var selectedOptions by remember(index) { mutableStateOf(setOf<String>()) }
                Column(
                    modifier = modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Text(
                            text = label,
                            style = MaterialTheme.typography.bodyMedium,
                            color = if (isError) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.onSurface,
                        )
                        if (isError) {
                            Spacer(modifier = Modifier.width(4.dp))
                            Icon(
                                imageVector = vectorResource(Res.drawable.ic_warning),
                                contentDescription = stringResource(Res.string.warning_icon_cd),
                                tint = MaterialTheme.colorScheme.error,
                            )
                        }
                    }
                    if (isError) {
                        Text(
                            text = stringResource(Res.string.field_required),
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.error,
                        )
                    }
                    options.forEach { option ->
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Checkbox(
                                checked = selectedOptions.contains(option),
                                onCheckedChange = { checked ->
                                    selectedOptions = if (checked) {
                                        selectedOptions + option
                                    } else {
                                        selectedOptions - option
                                    }
                                    onValueChange(selectedOptions.joinToString(", "))
                                },
                            )
                            Spacer(modifier = Modifier.width(8.dp))
                            Text(option)
                        }
                    }
                }
            }
        }

        MembershipDataTypes.BOOLEAN -> {
            var value by remember(index) { mutableStateOf(false) }
            Column(modifier = modifier.fillMaxWidth()) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Text(
                        text = label,
                        modifier = Modifier.weight(1f),
                        color = if (isError) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.onSurface,
                    )
                    if (isError) {
                        Icon(
                            imageVector = vectorResource(Res.drawable.ic_warning),
                            contentDescription = stringResource(Res.string.warning_icon_cd),
                            tint = MaterialTheme.colorScheme.error,
                        )

                        Spacer(modifier = Modifier.width(8.dp))
                    }
                    Switch(
                        checked = value,
                        onCheckedChange = {
                            value = it
                            onValueChange(it.toString())
                        },
                    )
                }
                if (isError) {
                    Text(
                        text = stringResource(Res.string.field_required),
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.error,
                    )
                }
            }
        }
    }
}
